'use client';

import { useLabel } from '@/helpers/useLabel';
import type { CSSProperties } from 'react';
import { Crown, Shield, ShieldCheck, User, Link as LinkIcon, Gem, Handshake, Headset, Wrench, Trophy, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { normalizeRole } from '@/lib/auth/roles';

type RoleConfig = {
  label: string;
  icon: LucideIcon;
  /** Tailwind classes that produce a soft pill in the role's color. */
  className?: string;
  /** Inline colors for roles whose color isn't on the Tailwind palette. */
  style?: CSSProperties;
  /** Plain color name (legacy callers). */
  color: string;
};

// Builds the soft-pill colors used by the custom role badges from one RGB color.
function pillStyle(rgb: [number, number, number], text: [number, number, number]): CSSProperties {
  const [r, g, b] = rgb;
  return {
    backgroundColor: `rgba(${r}, ${g}, ${b}, 0.15)`,
    color: `rgb(${text.join(', ')})`,
    borderColor: `rgba(${r}, ${g}, ${b}, 0.4)`,
    ['--tw-ring-color' as string]: `rgba(${r}, ${g}, ${b}, 0.3)`,
  };
}

const ROLE_CONFIG: Record<string, RoleConfig> = {
  owner: {
    label: 'Owner',
    icon: Crown,
    color: 'gold',
    className:
      'bg-amber-400/15 text-amber-300 border-amber-400/40 ring-1 ring-inset ring-amber-400/30',
  },
  manager_muff: {
    label: 'Manager Muff',
    icon: Trophy,
    color: 'violet',
    className: 'ring-1 ring-inset',
    style: pillStyle([96, 41, 224], [183, 159, 241]),
  },
  head_of_support: {
    label: 'Head of Support',
    icon: Headset,
    color: 'pink',
    className: 'ring-1 ring-inset',
    style: pillStyle([236, 72, 153], [246, 173, 209]),
  },
  admin: {
    label: 'Admin',
    icon: Shield,
    color: 'red',
    className:
      'bg-red-500/15 text-red-300 border-red-500/40 ring-1 ring-inset ring-red-500/30',
  },
  developer: {
    label: 'Developer',
    icon: Wrench,
    color: 'red',
    className: 'ring-1 ring-inset',
    style: pillStyle([189, 0, 0], [225, 140, 140]),
  },
  staff: {
    label: 'Staff',
    icon: ShieldCheck,
    color: 'blue',
    className:
      'bg-sky-500/15 text-sky-300 border-sky-500/40 ring-1 ring-inset ring-sky-500/30',
  },
  booster: {
    label: 'Booster',
    icon: Gem,
    color: 'pink',
    className:
      'bg-pink-500/15 text-pink-300 border-pink-500/40 ring-1 ring-inset ring-pink-500/30',
  },
  friend: {
    label: 'Friend',
    icon: Handshake,
    color: 'yellow',
    className: 'ring-1 ring-inset',
    style: pillStyle([234, 179, 8], [246, 221, 144]),
  },
  linked: {
    label: 'Linked',
    icon: LinkIcon,
    color: 'green',
    className:
      'bg-emerald-500/15 text-emerald-300 border-emerald-500/40 ring-1 ring-inset ring-emerald-500/30',
  },
  default: {
    label: 'Member',
    icon: User,
    color: 'gray',
    className:
      'bg-muted text-muted-foreground border-border ring-1 ring-inset ring-border',
  },
};

type Size = 'xs' | 'sm' | 'md';

const SIZE_CLASSES: Record<Size, { pill: string; icon: number }> = {
  xs: { pill: 'h-5 px-1.5 text-[10px] gap-1', icon: 10 },
  sm: { pill: 'h-6 px-2 text-xs gap-1', icon: 12 },
  md: { pill: 'h-7 px-2.5 text-sm gap-1.5', icon: 14 },
};

export default function RoleBadge({
  role,
  size = 'xs',
  showDefault = false,
  className,
}: {
  role?: string | null;
  size?: Size;
  showDefault?: boolean;
  className?: string;
}) {
  const tl = useLabel();
  if (!role || (role === 'default' && !showDefault)) return null;

  const config = ROLE_CONFIG[normalizeRole(role)];
  const Icon = config.icon;
  const sizeCls = SIZE_CLASSES[size] || SIZE_CLASSES.xs;

  return (
    <span
      className={cn(
        'inline-flex items-center justify-center rounded-md border font-medium leading-none whitespace-nowrap',
        sizeCls.pill,
        config.className,
        className
      )}
      style={config.style}
    >
      <Icon size={sizeCls.icon} className="shrink-0" />
      {tl(config.label)}
    </span>
  );
}

export function getRoleColor(role?: string | null) {
  return ROLE_CONFIG[normalizeRole(role)].color;
}

export function getRoleLabel(role?: string | null) {
  return ROLE_CONFIG[normalizeRole(role)].label;
}
